<script setup lang="ts">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import LineItemImage from '@/components/cart/added-modal/LineItemImage.vue';
import LineItemPrice from '@/components/cart/added-modal/LineItemPrice.vue';
import QuantitySelector from '@/components/cart/QuantitySelector.vue';
import { useCart } from '@/composables/useCart';
import { getAutoDeliveryInterval, NutsLineItem } from '@/lib/cart/lineItem';
import { getMaxPiecesPerOrder, shouldLinkToPdp } from '@/utils/cart';

const props = defineProps<{
  lineItemWithQuantitySubset: NutsLineItem;
  loadExpansionsPending: boolean;
}>();

const emit = defineEmits(['handle-close']);

const maximumPiecesPerOrder = computed<number | undefined>(() =>
  getMaxPiecesPerOrder(props.lineItemWithQuantitySubset),
);

const store = useStore();
const { preCheckoutLineItems, updateLineItem } = useCart(store);

const autoDeliveryIntervalWeeks = computed(() =>
  getAutoDeliveryInterval(props.lineItemWithQuantitySubset),
);

const quantityInCart = computed(() => {
  const lineItem = preCheckoutLineItems.value.find(
    (li) => li.id === props.lineItemWithQuantitySubset.id,
  );
  return lineItem?.quantity ?? props.lineItemWithQuantitySubset.quantity;
});

const currentQuantity = ref(props.lineItemWithQuantitySubset.quantity);

const quantity = computed({
  get: () => currentQuantity.value,
  set: (val) => {
    const difference = quantityInCart.value - currentQuantity.value;
    updateLineItem(
      props.lineItemWithQuantitySubset.id,
      {
        quantity: val + difference,
      },
      val,
    );
    currentQuantity.value = val;
  },
});
</script>

<template>
  <div
    class="inline-flex gap-x-3 items-start px-4 mt-4 w-full md:items-center"
    data-test="inline-product"
  >
    <LineItemImage
      :isPending="loadExpansionsPending"
      :linksToPdp="shouldLinkToPdp(lineItemWithQuantitySubset)"
      :name="lineItemWithQuantitySubset.name"
      :productPath="lineItemWithQuantitySubset.productPath"
      :titleImage="lineItemWithQuantitySubset.titleImage?.small"
      @handle-close="emit('handle-close')"
    />
    <div>
      <BaseBodyText class="font-semibold text-black">
        {{ lineItemWithQuantitySubset.name.en }}
      </BaseBodyText>
      <SmallBodyText class="mt-0.5 text-neutral-600">
        <template v-if="autoDeliveryIntervalWeeks">
          Auto-Delivery -
          <b class="font-semibold">{{ autoDeliveryIntervalWeeks }} Week Frequency</b>
        </template>
        <template v-else>
          {{ lineItemWithQuantitySubset.variant.variantName?.en }} -
          {{ money(lineItemWithQuantitySubset.piecePrice) }} each
        </template>
      </SmallBodyText>
      <div class="flex items-center mt-2">
        <QuantitySelector
          v-model="quantity"
          class="max-w-[6rem] min-w-[6rem]"
          hasBorderRadius
          :id="lineItemWithQuantitySubset.id"
          :maximumPiecesPerOrder
          data-promo="1"
          data-promo-creative="Add to cart modal"
          data-promo-name="Quantity selected"
          data-test="quantity-selector"
        />
        <LineItemPrice
          class="ml-3"
          :prices="lineItemWithQuantitySubset.variant.prices"
          :quantity="currentQuantity"
          :totalPriceBeforeCartLevelDiscount="
            lineItemWithQuantitySubset.totalPriceBeforeCartLevelDiscount
          "
          :totalSavings="lineItemWithQuantitySubset.totalSavings"
        />
      </div>
    </div>
  </div>
</template>
